import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Link } from '@remix-run/react';
import type { MetaFunction } from '@remix-run/node';
import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/shared/components/performance.css";
import "~/features/resume/section-order.css";
import "~/features/resume/layout-tab.css";
import "~/features/resume/resume-design-principles.css";
import "~/features/invoice/invoice.css";
import "~/features/invoice/advanced-invoice-preview.css";
import AdvancedInvoicePreview from '~/features/invoice/AdvancedInvoicePreview';
import InvoiceATSInfo from '~/features/invoice/InvoiceATSInfo';
import BasicInfoTab from '~/features/invoice/BasicInfoTab';
import ClientInfoTab from '~/features/invoice/ClientInfoTab';
import ItemsTab from '~/features/invoice/ItemsTab';
import SummaryTab from '~/features/invoice/SummaryTab';
import { invoiceTemplates, type InvoiceTemplate } from '~/shared/components/InvoiceTemplates';
import type { InvoiceData, InvoiceItem } from '~/shared/types/invoice';
import invoiceTranslations from '~/shared/utils/invoice';


// ============================================================================
// SEO METADATA - تحسين محركات البحث
// ============================================================================

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('invoice');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('invoice');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "مولد الفواتير الاحترافي - أسامة الزبيدي | أنشئ فواتير احترافية مجاناً | Invoice Generator - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "مولد الفواتير الاحترافي - أسامة الزبيدي | أنشئ فواتير احترافية مجاناً" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/invoice" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "مولد الفواتير الاحترافي - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "مولد الفواتير الاحترافي - أسامة الزبيدي | أنشئ فواتير احترافية مجاناً" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "مولد الفواتير الاحترافي - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/invoice" },
    { name: "alternate", content: "https://oz.sa/invoice", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/invoice", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/invoice", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// ============================================================================
// الترجمات - دعم اللغتين العربية والإنجليزية
// ============================================================================

const translations = invoiceTranslations;

// ============================================================================
// المكون الرئيسي
// ============================================================================

const InvoiceGenerator: React.FC = () => {
  // State Management
  const [uiLang, setUiLang] = useState<'ar' | 'en'>('ar');
  const [showPreview, setShowPreview] = useState(false);
  const [showATSInfo, setShowATSInfo] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate>(invoiceTemplates[0]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageType, setSuccessMessageType] = useState<'template' | 'demo'>('template');

  // Invoice Data
  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyLogo: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'SAR',
    taxRate: 15,
    discount: 0,
    clientName: '',
    clientAddress: '',
    clientPhone: '',
    clientEmail: '',
    shippingName: '',
    shippingAddress: '',
    shippingPhone: '',
    shippingEmail: '',
    items: [],
    notes: '',
    paymentTerms: '',
    companyTaxNumber: ''
  });

  const t = translations[uiLang];

  // Define tabs after t is available
  const tabs = [
    { id: 0, label: t.tabs.basic, icon: '🏢' },
    { id: 1, label: t.tabs.client, icon: '👤' },
    { id: 2, label: t.tabs.items, icon: '📦' },
    { id: 3, label: t.tabs.summary, icon: '📊' }
  ];

  // Handlers
  const handleFieldChange = useCallback((field: string, value: string | number) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLogoChange = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      handleFieldChange('companyLogo', e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, [handleFieldChange]);

  const handleAddItem = useCallback(() => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      price: 0,
      notes: ''
    };
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  }, []);

  const handleRemoveItem = useCallback((id: string) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  }, []);

  const handleUpdateItem = useCallback((id: string, field: string, value: string | number) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  }, []);

  const handleReorderItems = useCallback((newOrder: InvoiceItem[]) => {
    setInvoiceData(prev => ({
      ...prev,
      items: newOrder
    }));
  }, []);

  const generateDemoData = useCallback(() => {
    const demoData: InvoiceData = {
      companyName: 'شركة التقنية المتقدمة',
      companyAddress: 'شارع الملك فهد، الرياض، المملكة العربية السعودية',
      companyPhone: '+966 11 234 5678',
      companyEmail: 'info@techcompany.com',
      companyLogo: '',
      invoiceNumber: 'INV-2024-001',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      currency: 'SAR',
      taxRate: 15,
      discount: 0,
      clientName: 'شركة العميل المميز',
      clientAddress: 'شارع التحلية، جدة، المملكة العربية السعودية',
      clientPhone: '+966 12 345 6789',
      clientEmail: 'contact@clientcompany.com',
      shippingName: 'شركة العميل المميز',
      shippingAddress: 'شارع التحلية، جدة، المملكة العربية السعودية',
      shippingPhone: '+966 12 345 6789',
      shippingEmail: 'shipping@clientcompany.com',
      items: [
        {
          id: '1',
          description: 'تطوير موقع إلكتروني احترافي',
          quantity: 1,
          price: 5000,
          notes: 'يشمل التصميم والبرمجة والاختبار'
        },
        {
          id: '2',
          description: 'خدمات الصيانة الشهرية',
          quantity: 12,
          price: 500,
          notes: 'صيانة دورية وتحديثات أمنية'
        },
        {
          id: '3',
          description: 'استضافة خادم سحابي',
          quantity: 1,
          price: 1200,
          notes: 'استضافة سنوية مع دعم فني'
        }
      ],
      notes: 'شكراً لثقتكم في خدماتنا. نلتزم بتقديم أفضل الحلول التقنية.',
      paymentTerms: 'الدفع خلال 30 يوم من تاريخ الفاتورة',
      companyTaxNumber: ''
    };
    setInvoiceData(demoData);
  }, []);

  const clearAllData = useCallback(() => {
    if (confirm(t.actions.clearData)) {
      setInvoiceData({
        companyName: '',
        companyAddress: '',
        companyPhone: '',
        companyEmail: '',
        companyLogo: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        currency: 'SAR',
        taxRate: 15,
        discount: 0,
        clientName: '',
        clientAddress: '',
        clientPhone: '',
        clientEmail: '',
        shippingName: '',
        shippingAddress: '',
        shippingPhone: '',
        shippingEmail: '',
        items: [],
        notes: '',
        paymentTerms: '',
        companyTaxNumber: ''
      });
    }
  }, [t.actions.clearData]);





  // ===== تبديل اللغة =====
  const toggleLanguage = useCallback(() => setUiLang(l => l === 'ar' ? 'en' : 'ar'), []);

  // ===== معالجات القوالب =====
  const handleTemplateSelect = useCallback((template: InvoiceTemplate) => {
    setSelectedTemplate(template);
    setShowTemplateSelector(false);
    setSuccessMessageType('template');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  }, []);

  const toggleTemplateSelector = useCallback(() => {
    setShowTemplateSelector(v => !v);
  }, []);

  // ===== رسالة النجاح =====
  const SuccessMessage = useMemo(() => {
    const SuccessMessageComponent = () => (
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-medium">
          {successMessageType === 'demo' 
            ? (uiLang === 'ar' 
                ? 'تم توليد البيانات التجريبية بنجاح! يمكنك الآن معاينة الفاتورة.'
                : 'Demo data generated successfully! You can now preview your invoice.'
              )
            : (uiLang === 'ar' 
                ? `تم اختيار قالب "${selectedTemplate.name[uiLang]}" بنجاح!`
                : `Template "${selectedTemplate.name[uiLang]}" selected successfully!`
              )
          }
        </span>
      </motion.div>
    );
    SuccessMessageComponent.displayName = 'SuccessMessage';
    return SuccessMessageComponent;
  }, [uiLang, selectedTemplate.name, successMessageType]);

  return (
    <div className="invoice-app" dir={uiLang === 'ar' ? 'rtl' : 'ltr'}>
      {/* رسالة النجاح */}
      <AnimatePresence>
        {showSuccessMessage && <SuccessMessage />}
      </AnimatePresence>

      {/* Header with Glass Effect - مطابق لحاسبة العمر */}
      <div className="invoice-header">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="invoice-btn"
              aria-label={uiLang === 'ar' ? "العودة إلى الصفحة الرئيسية" : "Back to Main Page"}
            >
              {uiLang === 'ar' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h17" />
                </svg>
              )}
            </Link>
            
            <div className="flex items-center gap-2">
              <button
                onClick={toggleLanguage}
                className="lang-switcher"
                aria-label={uiLang === 'ar' ? "Switch to English" : "التبديل إلى العربية"}
              >
                {uiLang === 'ar' ? 'EN' : 'عربي'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-white mb-4">
            {t.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* اختيار القالب */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white text-center flex-1">
              {showTemplateSelector ? t.selectTemplate : (uiLang === 'ar' ? 'القالب المختار' : 'Selected Template')}
            </h2>
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              onClick={() => setShowTemplateSelector(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-lg hover:shadow-xl ml-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {uiLang === 'ar' ? 'تغيير القالب' : 'Change Template'}
            </motion.button>
          </div>
          <AnimatePresence mode="wait">
            {showTemplateSelector ? (
              <motion.div
                key="template-grid"
                initial={{ opacity: 0, height: 0, scale: 0.9 }}
                animate={{ opacity: 1, height: 'auto', scale: 1 }}
                exit={{ opacity: 0, height: 0, scale: 0.9 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="template-grid-optimized template-grid-layout-optimized grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
                  {invoiceTemplates.map((template, index) => (
                    <motion.button
                      key={template.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      onClick={() => {
                        handleTemplateSelect(template);
                        setShowTemplateSelector(false);
                      }}
                      className={`template-card-optimized template-card-interaction-optimized bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border-2 ${
                        selectedTemplate.id === template.id 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 template-selection-optimized selected' 
                          : template.isPremium
                            ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                            : 'border-transparent hover:border-blue-300 dark:hover:border-blue-600'
                      }`}
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${template.color} flex items-center justify-center text-3xl relative`}>
                        {template.icon}
                        {template.isPremium && (
                          <div className="absolute -top-1 -right-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs px-2 py-1 rounded-full font-bold shadow-lg">
                            👑
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 dark:text-white">
                          {template.name[uiLang as 'ar' | 'en']}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {template.description[uiLang as 'ar' | 'en']}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {uiLang === 'ar' ? 'درجة ATS' : 'ATS Score'}
                          </span>
                          <div className="flex items-center gap-2">
                            {template.isPremium && (
                              <span className="text-xs bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2 py-1 rounded-full font-bold">
                                {template.price}
                              </span>
                            )}
                            <span className={`text-xs font-medium ${
                              template.atsCompatibility.score >= 95 ? 'text-green-600 dark:text-green-400' :
                              template.atsCompatibility.score >= 85 ? 'text-blue-600 dark:text-blue-400' :
                              template.atsCompatibility.score >= 75 ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {template.atsCompatibility.score}/100
                            </span>
                          </div>
                        </div>
                        {selectedTemplate.id === template.id && (
                          <div className="mt-2 flex items-center justify-center">
                            <span className="text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                              {uiLang === 'ar' ? 'مختار' : 'Selected'}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>

        {/* المحتوى الرئيسي */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* لوحة التخصيص */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                {t.customize}
              </h2>
              {!showTemplateSelector && (
                <button
                  onClick={toggleTemplateSelector}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {uiLang === 'ar' ? 'تغيير القالب' : 'Change'}
                </button>
              )}
            </div>

            {/* عرض القالب المختار */}
            <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                    <span className="text-xl">{selectedTemplate.icon}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                      {uiLang === 'ar' ? selectedTemplate.name.ar : selectedTemplate.name.en}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {uiLang === 'ar' ? selectedTemplate.description.ar : selectedTemplate.description.en}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {uiLang === 'ar' ? 'درجة ATS' : 'ATS Score'}
                    </div>
                    <div className="text-sm font-bold text-green-600 dark:text-green-400">
                      {selectedTemplate.atsCompatibility.score}/100
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowATSInfo(!showATSInfo)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{uiLang === 'ar' ? 'اعرف المزيد' : 'Know More'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* معلومات توافق ATS */}
            {showATSInfo && (
              <div className="mb-6">
                <InvoiceATSInfo
                  template={selectedTemplate}
                  language={uiLang}
                  isVisible={showATSInfo}
                  onToggleVisibility={() => setShowATSInfo(false)}
                />
              </div>
            )}

            {/* التنقل بين الألسنة */}
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-3">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 shadow-sm font-bold text-base tracking-tight focus:outline-none focus:ring-2 focus:ring-blue-400/40
                      ${activeTab === tab.id
                        ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/10 scale-105 shadow-lg text-blue-700 dark:text-blue-200'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-400 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 text-gray-700 dark:text-gray-200'}
                    `}
                    style={{ minHeight: 90 }}
                  >
                    <span className="text-2xl mb-1 group-hover:scale-110 transition-transform duration-200">
                      {tab.icon}
                    </span>
                    <span className="text-xs font-medium mt-1">
                      {tab.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* محتوى الألسنة */}
            <div className="space-y-6">
              <AnimatePresence mode="wait">
                {activeTab === 0 && (
                  <motion.div
                    key="basic"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <BasicInfoTab
                      companyName={invoiceData.companyName}
                      companyAddress={invoiceData.companyAddress}
                      companyPhone={invoiceData.companyPhone}
                      companyEmail={invoiceData.companyEmail}
                      companyTaxNumber={invoiceData.companyTaxNumber}
                      companyLogo={invoiceData.companyLogo}
                      invoiceNumber={invoiceData.invoiceNumber}
                      invoiceDate={invoiceData.invoiceDate}
                      dueDate={invoiceData.dueDate}
                      currency={invoiceData.currency}
                      onChange={handleFieldChange}
                      onLogoChange={handleLogoChange}
                      t={t.basic}
                    />
                  </motion.div>
                )}
                
                {activeTab === 1 && (
                  <motion.div
                    key="client"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ClientInfoTab
                      clientName={invoiceData.clientName}
                      clientAddress={invoiceData.clientAddress}
                      clientPhone={invoiceData.clientPhone}
                      clientEmail={invoiceData.clientEmail}
                      shippingName={invoiceData.shippingName || ''}
                      shippingAddress={invoiceData.shippingAddress || ''}
                      shippingPhone={invoiceData.shippingPhone || ''}
                      shippingEmail={invoiceData.shippingEmail || ''}
                      onChange={handleFieldChange}
                      t={t.client}
                    />
                  </motion.div>
                )}
                
                {activeTab === 2 && (
                  <motion.div
                    key="items"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ItemsTab
                      items={invoiceData.items}
                      onAddItem={handleAddItem}
                      onRemoveItem={handleRemoveItem}
                      onUpdateItem={handleUpdateItem}
                      onReorderItems={handleReorderItems}
                      t={t.items}
                    />
                  </motion.div>
                )}
                
                {activeTab === 3 && (
                  <motion.div
                    key="summary"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <SummaryTab
                      taxRate={invoiceData.taxRate}
                      discount={invoiceData.discount}
                      notes={invoiceData.notes || ''}
                      paymentTerms={invoiceData.paymentTerms || ''}
                      onChange={handleFieldChange}
                      onGenerateDemo={generateDemoData}
                      onClearAll={clearAllData}
                      t={t.summary}
                      subtotal={invoiceData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0)}
                      currency={invoiceData.currency}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* معاينة الفاتورة المتقدمة - مطابقة لمعاينة السيرة الذاتية */}
          <div style={{ width: '100%' }}>
            {showPreview && (
              <AdvancedInvoicePreview
                invoice={invoiceData}
                selectedTemplate={selectedTemplate}
                uiLang={uiLang}
                t={t}
                onExportPDF={async (data) => {
                  try {
                    console.log('Exporting invoice PDF:', data);
                    // يمكن إضافة منطق إضافي هنا إذا لزم الأمر
                  } catch (error) {
                    console.error('Invoice PDF export failed:', error);
                    alert(uiLang === 'ar' ? 'فشل في تصدير PDF' : 'PDF export failed');
                  }
                }}
                onExportJSON={(data) => {
                  try {
                    console.log('Exporting invoice JSON:', data);
                    // يمكن إضافة منطق إضافي هنا إذا لزم الأمر
                  } catch (error) {
                    console.error('Invoice JSON export failed:', error);
                    alert(uiLang === 'ar' ? 'فشل في تصدير JSON' : 'JSON export failed');
                  }
                }}
              />
            )}
            <div className="flex items-center gap-2 mt-6">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {showPreview ? <FaEyeSlash /> : <FaEye />}
                {showPreview ? t.previewControls.hidePreview : t.previewControls.showPreview}
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default InvoiceGenerator; 